import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis as RechartsPolarAngleAxis,
  PolarRadiusAxis as RechartsPolarRadiusAxis,
  Radar,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from 'recharts';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { surveyAPI, teamAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Survey, SurveyResponse } from '../types';
import '../styles/survey-responses.css';

interface ResponseData {
  survey: {
    _id: string;
    title: string;
    anonymousResponses: boolean;
  };
  responses: SurveyResponse[];
  totalResponses: number;
  target?: number;
  completionRate?: number | null;
}

// Workaround for TS2786 JSX typing issue in some Recharts versions
const PolarAngleAxisAny: any = RechartsPolarAngleAxis as any;
const PolarRadiusAxisAny: any = RechartsPolarRadiusAxis as any;

// Color thresholds helper for score 0-100 - matching dashboard colors
const colorForScore = (val: number) => {
  if (val <= 30) return { fg: '#FFC9C9', bg: 'rgba(229,57,53,0.25)', text: '#e53935' }; // red
  if (val <= 45) return { fg: '#FFE2AD', bg: 'rgba(251,140,0,0.25)', text: '#F7841B' }; // orange
  if (val <= 63) return { fg: '#FFF5B8', bg: 'rgba(253,216,53,0.25)', text: '#F7C100' }; // yellow (updated text color to match dashboard)
  if (val <= 79) return { fg: '#BEEFD9', bg: 'rgba(129,199,132,0.25)', text: '#40A678' }; // light green
  return { fg: '#0CF78D', bg: 'rgba(67,160,71,0.25)', text: '#007E46' }; // green
};

// SVG ring for overall average score 0-100 (aligned with dashboard ring colors)
const ScoreRing: React.FC<{ score: number; size?: number; stroke?: number }> = ({ score, size = 120, stroke = 10 }) => {
  const val = Math.max(0, Math.min(100, Math.floor(score)));
  const { fg, bg, text } = colorForScore(val);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - val / 100);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} stroke={bg} strokeWidth={stroke} fill="none" strokeLinecap="round" />
        <circle
          cx={size/2}
          cy={size/2}
          r={radius}
          stroke={text}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: text, fontSize: 28, fontWeight: 700 }}>
        {val}
      </Box>
    </Box>
  );
};

const SurveyResponses: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState<ResponseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [surveyDetail, setSurveyDetail] = useState<Survey | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);
  const [assignees, setAssignees] = useState<string[]>([]); // userIds
  const [logsExpanded, setLogsExpanded] = useState(false);

  useEffect(() => {
    fetchResponses();
    fetchSurveyDetail();
    const iv = setInterval(() => fetchResponses(true), 10000); // poll every 10s for live updates
    return () => clearInterval(iv);
  }, [id]);

  const fetchResponses = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await surveyAPI.getSurveyResponses(id!);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch responses');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchSurveyDetail = async () => {
    try {
      const res = await surveyAPI.getSurvey(id!);
      setSurveyDetail(res.data as Survey);
      // Build assigned user id list (assignedUsers + team members)
      const s: any = res.data;
      const directUserIds: string[] = (s.assignedUsers || []).map((u: any) => (typeof u === 'string' ? u : u._id)).filter(Boolean);
      const teamIds: string[] = (s.assignedTeams || []).map((t: any) => (typeof t === 'string' ? t : t._id)).filter(Boolean);
      let teamMemberIds: string[] = [];
      if (teamIds.length) {
        try {
          const teamsRes = await teamAPI.getTeams();
          const allTeams = teamsRes.data || [];
          const selectedTeams = allTeams.filter((t: any) => teamIds.includes(String(t._id)));
          selectedTeams.forEach((t: any) => {
            (t.members || []).forEach((m: any) => {
              const id = typeof m === 'string' ? m : m._id;
              if (id) teamMemberIds.push(String(id));
            });
          });
        } catch (_) {
          // best-effort; ignore if teams fetch fails
        }
      }
      const unique = Array.from(new Set([...directUserIds, ...teamMemberIds]));
      setAssignees(unique);
    } catch (_) {
      // best-effort only
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTimeSpent = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const questionTextById = useMemo(() => {
    const map = new Map<string, string>();
    if (surveyDetail?.questions) {
      (surveyDetail.questions as any[]).forEach((q: any) => {
        map.set(String(q._id), q.questionText || q.text || q.title || 'Question');
      });
    }
    return map;
  }, [surveyDetail]);

  // Determine if this survey is a 1-2-1 (assigned to exactly one user, no teams)
  const isOneToOneSurvey = useMemo(() => {
    const s: any = surveyDetail;
    if (!s) return false;
    const au = Array.isArray(s?.assignedUsers) ? s.assignedUsers : [];
    const at = Array.isArray(s?.assignedTeams) ? s.assignedTeams : [];
    return au.length === 1 && at.length === 0;
  }, [surveyDetail]);

  const questionSectionById = useMemo(() => {
    const map = new Map<string, string | null>();
    if (surveyDetail?.questions) {
      (surveyDetail.questions as any[]).forEach((q: any) => {
        map.set(String(q._id), q.sectionId ? String(q.sectionId) : null);
      });
    }
    return map;
  }, [surveyDetail]);

  const sectionsOrdered = useMemo(() => {
    const secs = (surveyDetail?.sections as any[]) || [];
    return secs.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [surveyDetail]);

  // Map questionId -> category
  const categoryByQuestion = useMemo(() => {
    const map = new Map<string, string | undefined>();
    if (surveyDetail?.questions) {
      (surveyDetail.questions as any[]).forEach((q: any) => {
        map.set(String(q._id), q.category || undefined);
      });
    }
    return map;
  }, [surveyDetail]);


  // Build answers grouped by questionId for breakdown (hook must not be conditional)
  const answersByQuestion = useMemo(() => {
    const map = new Map<string, any[]>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a) => {
        const key = String(a.questionId);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(a.answer);
      });
    });
    return map;
  }, [data?.responses]);

  // Chip props for score 0-100
  const renderScoreChip = (score?: number) => {
    if (typeof score !== 'number' || isNaN(score)) return null;
    const val = Math.max(0, Math.min(100, Math.floor(score)));
    let bgcolor = undefined as any;
    let color = undefined as any;
    if (val <= 30) { bgcolor = '#FFC9C9'; color = '#EA5354'; } // red
    else if (val <= 45) { bgcolor = '#FFE7D0'; color = '#FF7A00'; } // orange
    else if (val <= 63) { bgcolor = '#FFF5B8'; color = '#F0B400'; } // yellow
    else if (val <= 79) { bgcolor = '#BEEFD9'; color = '#40A678'; } // light green
    else { bgcolor = '#0CF78D'; color = '#007E46'; } // green
    return (
      <Chip
        label={`${val}`}
        size="small"
        sx={{ ml: 2, bgcolor, color, fontWeight: 800 }}
      />
    );
  };

  // Average points per question (uses answer.points computed server-side)
  const avgPointsByQuestion = useMemo(() => {
    const sums = new Map<string, { sum: number; n: number }>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a) => {
        const key = String(a.questionId);
        const pts = typeof (a as any).points === 'number' ? Number((a as any).points) : 0;
        const cur = sums.get(key) || { sum: 0, n: 0 };
        sums.set(key, { sum: cur.sum + pts, n: cur.n + 1 });
      });
    });
    const out = new Map<string, number>();
    Array.from(sums.entries()).forEach(([k, v]) => {
      out.set(k, v.n ? v.sum / v.n : 0);
    });
    return out;
  }, [data?.responses]);

  // Average points per section
  const avgPointsBySection = useMemo(() => {
    const secAgg = new Map<string, { sum: number; n: number }>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a) => {
        const qid = String(a.questionId);
        const secId = questionSectionById.get(qid);
        if (!secId) return;
        const pts = typeof (a as any).points === 'number' ? Number((a as any).points) : 0;
        const cur = secAgg.get(secId) || { sum: 0, n: 0 };
        secAgg.set(secId, { sum: cur.sum + pts, n: cur.n + 1 });
      });
    });
    const out = new Map<string, number>();
    Array.from(secAgg.entries()).forEach(([k, v]) => {
      out.set(k, v.n ? v.sum / v.n : 0);
    });
    return out;
  }, [data?.responses, questionSectionById]);

  // Average points per category
  const avgPointsByCategory = useMemo(() => {
    const catAgg = new Map<string, { sum: number; n: number }>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a) => {
        const qid = String(a.questionId);
        const cat = categoryByQuestion.get(qid);
        if (!cat) return;
        const pts = typeof (a as any).points === 'number' ? Number((a as any).points) : 0;
        const cur = catAgg.get(cat) || { sum: 0, n: 0 };
        catAgg.set(cat, { sum: cur.sum + pts, n: cur.n + 1 });
      });
    });
    const out = new Map<string, number>();
    Array.from(catAgg.entries()).forEach(([k, v]) => {
      out.set(k, v.n ? v.sum / v.n : 0);
    });
    return out;
  }, [data?.responses, categoryByQuestion]);

  // Prepare radar (spider) chart data from category averages
  const radarData = useMemo(() => {
    const entries = Array.from(avgPointsByCategory.entries()).sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([cat, avg]) => ({
      category: cat,
      score: Math.max(0, Math.min(100, Number(avg) || 0)),
    }));
  }, [avgPointsByCategory]);

  // Flatten category scores into an array for highlights/lowlights
  const categoryScoreList = useMemo(() => {
    const entries = Array.from(avgPointsByCategory.entries());
    return entries
      .map(([cat, avg]) => ({
        category: cat,
        score: Math.max(0, Math.min(100, Number(avg) || 0)),
      }))
      .sort((a, b) => b.score - a.score); // highest first
  }, [avgPointsByCategory]);

  const highlightItems = useMemo(() => {
    if (!categoryScoreList.length) return [] as { category: string; score: number }[];
    return categoryScoreList.slice(0, 3);
  }, [categoryScoreList]);

  const lowlightItems = useMemo(() => {
    if (!categoryScoreList.length) return [] as { category: string; score: number }[];
    const sortedAsc = [...categoryScoreList].sort((a, b) => a.score - b.score);
    return sortedAsc.slice(0, 3);
  }, [categoryScoreList]);

  // Overall engagement score: average of category scores (0-100)
  const overallEngagementScore = useMemo(() => {
    if (avgPointsByCategory.size === 0) return null;
    const values = Array.from(avgPointsByCategory.values()).filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!values.length) return null;
    const sum = values.reduce((acc, v) => acc + v, 0);
    const avg = sum / values.length;
    return Math.max(0, Math.min(100, avg));
  }, [avgPointsByCategory]);

  // Helper to robustly extract a comment from an answer payload (backend may use different keys)
  const extractComment = (a: any): string | undefined => {
    const cands = [
      a?.comment,
      a?.comments,
      a?.note,
      a?.notes,
      a?.commentText,
      a?.freeText,
      a?.feedback,
    ];
    const val = cands.find((v) => typeof v === 'string' && v.trim().length > 0);
    return typeof val === 'string' ? val.trim() : undefined;
  };

  // Detailed answers per question (answer value + comment)
  const answersDetailByQuestion = useMemo(() => {
    const map = new Map<string, Array<{ answer: any; comment?: string }>>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a: any) => {
        const key = String(a.questionId);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push({ answer: a.answer, comment: extractComment(a) });
      });
    });
    return map;
  }, [data?.responses]);

  // Count of comments per question
  const commentCountByQuestion = useMemo(() => {
    const map = new Map<string, number>();
    const responses = data?.responses ?? [];
    responses.forEach((r) => {
      r.answers.forEach((a: any) => {
        const key = String(a.questionId);
        const hasComment = !!extractComment(a);
        if (hasComment) {
          map.set(key, (map.get(key) || 0) + 1);
        }
      });
    });
    return map;
  }, [data?.responses]);

  // Derived completion based on assignees vs submitted responses
  const completionDerived = useMemo(() => {
    const total = assignees.length;
    if (total === 0) return { total: 0, completed: 0 };
    // If anonymous, we cannot map responders to assignees; prefer totalResponses (or responses.length)
    if (surveyDetail?.anonymousResponses) {
      const anonCount = (data?.totalResponses ?? data?.responses?.length ?? 0);
      return { total, completed: Math.min(anonCount, total) };
    }
    // Non-anonymous: consider multiple signals and take the max, then cap to total assignees
    const responderIds = new Set<string>();
    const responderIdsComplete = new Set<string>();
    (data?.responses || []).forEach((r) => {
      const uid = typeof r.user === 'string' ? r.user : (r.user as any)?._id;
      if (!uid) return;
      if (!assignees.includes(String(uid))) return;
      responderIds.add(String(uid));
      if ((r as any).isComplete) responderIdsComplete.add(String(uid));
    });
    const mappedCount = responderIds.size;
    const mappedCompleteCount = responderIdsComplete.size;
    const aggregateCount = data?.totalResponses ?? data?.responses?.length ?? 0;
    const completed = Math.min(Math.max(mappedCount, mappedCompleteCount, aggregateCount), total);
    return { total, completed };
  }, [assignees, data?.responses, data?.totalResponses, surveyDetail?.anonymousResponses]);

  const calculateAverageRating = () => {
    if (!data || data.responses.length === 0) return null;
    const ratingAnswers: number[] = [];
    data.responses.forEach((response) => {
      response.answers.forEach((answer) => {
        if (typeof answer.answer === 'number' && answer.answer >= 1 && answer.answer <= 5) {
          ratingAnswers.push(answer.answer);
        }
      });
    });
    if (ratingAnswers.length === 0) return null;
    const average = ratingAnswers.reduce((a, b) => a + b, 0) / ratingAnswers.length;
    return average.toFixed(2);
  };

  const averageRating = calculateAverageRating();
  const averageTimeSpent =
    data && data.responses.length > 0
      ? Math.floor(
          data.responses.reduce((sum, r) => sum + r.timeSpent, 0) / data.responses.length
        )
      : 0;

  const renderQuestionAggregate = (answers: any[]) => {
    if (!answers || answers.length === 0) return <Typography variant="body2" color="text.secondary">No responses yet</Typography>;
    const sample = answers.slice(0, 3);
    const types = new Set(sample.map((v) => Array.isArray(v) ? 'array' : typeof v));
    // Boolean breakdown
    if (types.size === 1 && types.has('boolean')) {
      const yes = answers.filter((v) => v === true).length;
      const no = answers.filter((v) => v === false).length;
      return (
        <Typography variant="body2">Yes: {yes} • No: {no}</Typography>
      );
    }
    // Number average
    if (Array.from(types).every((t) => t === 'number')) {
      const nums = answers as number[];
      const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
      return <Typography variant="body2">Average: {avg.toFixed(2)} (n={nums.length})</Typography>;
    }
    // Array (multi-select) - show top selections
    if (Array.from(types).every((t) => t === 'array')) {
      const freq = new Map<string, number>();
      (answers as any[][]).forEach((arr) => arr.forEach((opt) => freq.set(String(opt), (freq.get(String(opt)) || 0) + 1)));
      const top = Array.from(freq.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} (${v})`).join(', ');
      return <Typography variant="body2">Top selections: {top || '—'}</Typography>;
    }
    // Fallback: show sample answers
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">Sample answers:</Typography>
        {sample.map((ans, i) => (
          <Typography key={i} variant="body2">• {Array.isArray(ans) ? ans.join(', ') : String(ans)}</Typography>
        ))}
      </Box>
    );
  };

  // Get a human-friendly label for an answer value based on question metadata
  const getOptionLabel = (q: any, value: any): string => {
    if (typeof value === 'boolean') return value ? 'Strongly Agree' : 'Strongly Disagree'; // fallback, but boolean handled elsewhere
    if (!q) return String(value);
    const qType = q.questionType;
    if (qType === 'rating' && typeof value === 'number') {
      if (Array.isArray(q.ratingLabels) && q.ratingLabels[value - 1]) {
        return String(q.ratingLabels[value - 1]);
      }
      return String(value);
    }
    if (qType === 'multiple_choice') {
      // If value is numeric index, map to options (1-based or 0-based unknown)
      if (typeof value === 'number' && Array.isArray(q.options)) {
        // Try 1-based first, else 0-based
        const oneBased = q.options[value - 1];
        if (oneBased) return String(oneBased);
        const zeroBased = q.options[value];
        if (zeroBased) return String(zeroBased);
      }
      // If value is a string and present in options, return as-is
      if (typeof value === 'string') return String(value);
    }
    return String(value);
  };

  // All possible option labels for a question, in display order
  const getAllOptionLabels = (q: any): string[] => {
    if (!q) return [];
    const t = q.questionType;
    if (t === 'rating') {
      if (Array.isArray(q.ratingLabels) && q.ratingLabels.length > 0) return q.ratingLabels.map(String);
      const scale = typeof q.ratingScale === 'number' && q.ratingScale > 0 ? q.ratingScale : 5;
      return Array.from({ length: scale }, (_, i) => String(i + 1));
    }
    if (t === 'multiple_choice' && Array.isArray(q.options)) {
      return q.options.map(String);
    }
    if (t === 'boolean') {
      return ['Yes', 'No'];
    }
    return [];
  };

  // Build per-option counts for a set of answers (arrays are expanded)
  const renderOptionCounts = (q: any, answers: any[]) => {
    if (!answers || answers.length === 0) return null;
    const allLabels = getAllOptionLabels(q);
    const freq = new Map<string, number>();
    // Seed all options with 0 so we always show them
    allLabels.forEach((label: string) => freq.set(String(label), 0));
    answers.forEach((ans) => {
      if (Array.isArray(ans)) {
        ans.forEach((v) => {
          const k = getOptionLabel(q, v);
          freq.set(k, (freq.get(k) || 0) + 1);
        });
        return;
      }
      const t = typeof ans;
      if (t === 'boolean') {
        const k = ans ? 'Yes' : 'No';
        freq.set(k, (freq.get(k) || 0) + 1);
        return;
      }
      if (t === 'number' || t === 'string') {
        const k = getOptionLabel(q, ans);
        if (k.trim().length === 0) return;
        freq.set(k, (freq.get(k) || 0) + 1);
      }
    });
    // Use the "allLabels" order if available; otherwise, fallback to count-descending
    const rows = allLabels.length
      ? allLabels.map((label: string) => [label, freq.get(label) || 0] as [string, number])
      : Array.from(freq.entries()).sort((a, b) => b[1] - a[1]);
    if (rows.length === 0) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography className="responses-countsTitle" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Response counts:</Typography>
        <Box className="responsecounts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {rows.map(([label, count]) => (
            <Typography className="count" key={label} variant="body2">{label}: {count}</Typography>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Container className="responses-page" maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      <Typography className="responses-title" variant="h4" component="h1" gutterBottom>
        Survey Responses: {data?.survey?.title ?? ''}
      </Typography>

      {/* Categories Overview - moved to top (first section after title) */}
      {avgPointsByCategory.size > 0 && (
        <Box className="responses-overview" sx={{ mb: 4 }}>

          {/* Top row: two-column layout - left spider chart, right stacked stat cards */}
          <Grid className="responses-overviewGrid" container spacing={2} sx={{ mb: 2 }}>
            <Grid className="responses-overviewChartCol" item xs={12} md={7}>
              <Card className="responses-card responses-card--radar">
                <CardContent className="responses-cardContent">
                  <Typography className="responses-cardTitle" variant="subtitle1" gutterBottom>Category Scores (0 - 100)</Typography>
                  <Box className="responses-radarWrap" sx={{ width: '100%', height: 360 }}>
                    <ResponsiveContainer>
                      <RadarChart data={radarData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                        <PolarGrid stroke="#eaeaea" />
                        <PolarAngleAxisAny dataKey="category" tick={{ fontSize: 12, fill: '#000640', fontWeight: 500 }} />
                        <PolarRadiusAxisAny angle={30} domain={[0, 100]} tick={{ fontSize: 12 }} />
                        <Radar name="Average" dataKey="score" stroke="#BBC7FF" fill="#BBC7FF" fillOpacity={0.6} />
                        <RechartsTooltip
                          formatter={(value: any) => [`${Number(value).toFixed(1)}`, 'Score']}
                          itemStyle={{ color: '#000640' }}
                          labelStyle={{ color: '#000640', fontWeight: 600 }}
                          contentStyle={{ borderRadius: 4 }}
                        />
                        <RechartsLegend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid className="responses-overviewStatsCol" item xs={12} md={5}>
              <Box className="responses-statsGrid" sx={{ display: 'grid', gap: 2 }}>
                {overallEngagementScore !== null && (
                  <Card className="responses-card responses-card--overall">
                    <CardContent className="responses-cardContent" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box className="responses-cardText" sx={{ flexGrow: 1 }}>
                        <Typography className="responses-cardSubtitle" color="text.secondary" gutterBottom>
                          Engagement Score
                        </Typography>
                        <Typography className="responses-cardMeta" variant="body2" color="text.secondary">{overallEngagementScore.toFixed(0)} / 100</Typography>
                      </Box>
                      <ScoreRing size={72} score={overallEngagementScore} />
                    </CardContent>
                  </Card>
                )}

                <Card className="responses-card responses-card--completion">
                  <CardContent className="responses-cardContent">
                    <Box className="responses-statRow">
                      <Typography className="responses-statLabel" component="div">Total responses</Typography>
                      <Typography className="responses-statValue" component="div">
                        {completionDerived.total > 0 ? `${completionDerived.completed} / ${completionDerived.total}` : '—'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card className="responses-card responses-card--timespent">
                  <CardContent className="responses-cardContent">
                    <Box className="responses-statRow">
                      <Typography className="responses-statLabel" component="div">Av. Time to Complete</Typography>
                      <Typography className="responses-statValue" component="div">{formatTimeSpent(averageTimeSpent)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Highlights / Lowlights section based on category scores */}
      {categoryScoreList.length > 0 && (
        <Box className="responses-highlights" sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="responses-sectionTitle" variant="h5" sx={{ mb: 2 }}>Highlights</Typography>
              <Box className="responses-highlightsList">
                {highlightItems.map((item) => {
                  const colors = colorForScore(Math.floor(item.score));
                  const isHigh = item.score > 65;
                  return (
                    <Box key={item.category} className="responses-highlightCard">
                      <Box className="responses-highlightHeader">
                        <Box
                          className="responses-highlightDot"
                          sx={{ backgroundColor: colors.text }}
                        />
                        <Box className="responses-highlightText">
                          <Typography className="responses-highlightTitle" variant="subtitle1">
                            {item.category}
                          </Typography>
                          <Typography className="responses-highlightBody" variant="body2" color="text.secondary">
                            {isHigh
                              ? <>The team scores highly in {item.category}. Continue to build on this strength and share best practices across the organization.</>
                              : <>The team scores OK in {item.category}. This is a positive sign and can be built on further.</>}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="responses-sectionTitle" variant="h5" sx={{ mb: 2 }}>Lowlights</Typography>
              <Box className="responses-highlightsList responses-highlightsList--low">
                {lowlightItems.map((item) => {
                  const colors = colorForScore(Math.floor(item.score));
                  return (
                    <Box key={item.category} className="responses-highlightCard responses-highlightCard--low">
                      <Box className="responses-highlightHeader">
                        <Box
                          className="responses-highlightDot"
                          sx={{ backgroundColor: colors.text }}
                        />
                        <Box className="responses-highlightText">
                          <Typography className="responses-highlightTitle" variant="subtitle1">
                            {item.category}
                          </Typography>
                          <Typography className="responses-highlightBody" variant="body2" color="text.secondary">
                            The team is scoring lower in {item.category}. This may indicate an area that needs attention and targeted improvement.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {data?.survey?.anonymousResponses && (
        <Alert className="responses-anonAlert" severity="info" sx={{ mb: 3 }}>
          This survey has anonymous responses enabled. User information is hidden.
        </Alert>
      )}

      {/* Questions Breakdown - Accordions */}
      {surveyDetail?.questions && (
        <Box className="responses-breakdown" sx={{ mb: 4 }}>
          <Typography className="responses-sectionTitle" variant="h5" sx={{ mb: 2 }}>Questions Breakdown</Typography>
          <Box className="responses-accordionList">
            {(surveyDetail.questions as any[]).map((q: any, idx: number) => {
              const qid = String(q._id);
              const answers = answersByQuestion.get(qid) || [];
              const avgPts = avgPointsByQuestion.get(qid);
              const details = answersDetailByQuestion.get(qid) || [];
              const commentCount = details.reduce((acc, d) => acc + ((typeof d.comment === 'string' && d.comment.trim().length > 0) ? 1 : 0), 0);
              return (
                <Accordion className="responses-accordion" key={qid} defaultExpanded={false} disableGutters>
                  <AccordionSummary className="responses-accordionSummary" expandIcon={<ExpandMoreIcon />}>
                    <Box className="responses-accordionHeader" sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {typeof avgPts === 'number' && (
                          <Box
                            className="responses-scoreCircle"
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              backgroundColor: colorForScore(Math.floor(avgPts)).fg,
                              color: colorForScore(Math.floor(avgPts)).text,
                            }}
                          >
                            {Math.floor(avgPts)}
                          </Box>
                        )}
                        <Box className="responses-accordionHeaderText" sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography className="responses-questionTitle" variant="subtitle1">{q.questionText || q.text || q.title || `Question ${idx + 1}`}</Typography>
                          <Typography className="responses-questionMeta" variant="caption" color="text.secondary">
                            Total responses: {answers.length}
                          </Typography>
                        </Box>
                      </Box>
                      <Box className="responses-accordionHeaderChips" sx={{ display: 'flex', alignItems: 'center' }}>
                        {commentCount > 0 ? (
                          <Chip
                            label={`${commentCount} comment${commentCount > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className="responses-accordionDetails">
                    {/* Aggregate details hidden as requested */}
                    {renderOptionCounts(q, answers)}
                    {details.length === 0 ? (
                      <Typography className="responses-noAnswers" variant="body2" color="text.secondary">No responses yet</Typography>
                    ) : (
                      <Box className="responses-answersGrid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 1 }}>
                        {details.map((d, i) => (
                          <React.Fragment key={i}>
                            <Typography className="responses-answer" variant="body2" sx={d.comment ? { fontWeight: 600 } : undefined}>
                              {Array.isArray(d.answer) ? d.answer.join(', ') : typeof d.answer === 'boolean' ? (d.answer ? 'Yes' : 'No') : String(d.answer)}
                            </Typography>
                            <Typography className="responses-answerComment" variant="body2" color="text.secondary">
                              {d.comment ? `Comment: ${d.comment}` : ''}
                            </Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Box>
      )}

      


      {/* Sections Averages */}
      {sectionsOrdered.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Sections Overview</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {sectionsOrdered.map((sec: any) => {
              const avg = avgPointsBySection.get(String(sec._id));
              return (
                <Card key={sec._id}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>{sec.title || 'Untitled section'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg. points: {avg !== undefined ? avg.toFixed(2) : '—'}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 4, mb: 2 }}>
        <Box 
          onClick={() => setLogsExpanded(!logsExpanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
            mb: 2
          }}
        >
          <Typography className="responses-sectionTitle" variant="h5">Survey logs</Typography>
          <ExpandMoreIcon sx={{ 
            ml: 1, 
            transform: logsExpanded ? 'rotate(180deg)' : 'rotate(0)', 
            transition: 'transform 0.2s' 
          }} />
        </Box>
        <Collapse in={logsExpanded}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {!data?.survey?.anonymousResponses && (
                    <>
                      <TableCell>Respondent</TableCell>
                      <TableCell>Department</TableCell>
                    </>
                  )}
                  <TableCell>Submitted At</TableCell>
                  <TableCell>Time Spent</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.responses.map((response) => (
                  <TableRow key={response._id}>
                    {!data?.survey?.anonymousResponses && typeof response.user === 'object' && (
                      <>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2">{response.user.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {response.user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{response.user.department || '—'}</TableCell>
                      </>
                    )}
                    <TableCell>{formatDate(response.createdAt)}</TableCell>
                    <TableCell>{formatTimeSpent(response.timeSpent)}</TableCell>
                    <TableCell>
                      <Chip
                        label={response.isComplete ? 'Completed' : 'Incomplete'}
                        size="small"
                        color={response.isComplete ? 'success' : 'default'}
                        variant={response.isComplete ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedResponse(response);
                          setDetailsOpen(true);
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>

      {/* Response Details Dialog */}
      <Dialog className="responses-dialog" open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className="responses-dialogTitle">Response Details</DialogTitle>
        <DialogContent className="responses-dialogContent" dividers>
          {selectedResponse ? (
            <Box className="responses-dialogBody">
              {!data?.survey?.anonymousResponses && typeof selectedResponse.user === 'object' && (
                <Box className="responses-dialogRow" sx={{ mb: 2 }}>
                  <Typography className="responses-dialogLabel" variant="subtitle2">Respondent</Typography>
                  <Typography className="responses-dialogValue" variant="body2">{selectedResponse.user.name} ({selectedResponse.user.email})</Typography>
                </Box>
              )}
              <Typography className="responses-dialogLabel" variant="subtitle2" sx={{ mb: 1 }}>Time Spent</Typography>
              <Typography className="responses-dialogValue" variant="body2" sx={{ mb: 2 }}>{formatTimeSpent(selectedResponse.timeSpent)}</Typography>

              <Typography className="responses-dialogLabel" variant="subtitle2" sx={{ mb: 1 }}>Answers</Typography>
              <Box className="responses-dialogAnswersGrid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
                {selectedResponse.answers.map((a) => (
                  <React.Fragment key={a.questionId}>
                    <Typography className="responses-dialogAnswer" variant="body2" sx={{ fontWeight: 600 }}>
                      {questionTextById.get(String(a.questionId)) || 'Question'}
                    </Typography>
                    <Typography className="responses-dialogAnswerVal" variant="body2" color="text.secondary">
                      {Array.isArray(a.answer) ? a.answer.join(', ') : typeof a.answer === 'boolean' ? (a.answer ? 'Yes' : 'No') : String(a.answer)}
                    </Typography>
                    {(a as any).comment && (
                      <Typography className="responses-dialogAnswerComment" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Comment: {(a as any).comment}
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No response selected</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Get more help (Managers only, exclude 1-2-1s) */}
      {user?.role === 'manager' && !isOneToOneSurvey && (
        <Box sx={{ mt: 4 }}>
          <Typography className="responses-sectionTitle" variant="h5" sx={{ mb: 2 }}>Get more help</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>Speak to an HR expert</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get tailored guidance from a professional HR specialist to interpret results and plan next steps.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate('/messages')}>Get support</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>Order a bespoke action plan</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Receive a tailored action plan based on your survey insights to boost engagement.
                  </Typography>
                  <Button variant="outlined" onClick={() => navigate('/messages')}>Order plan</Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default SurveyResponses;
